/**
 * Helper functions for Season and Events management
 * These utilities handle data transformation for season updates
 */

/**
 * Checks if the updates object contains any base room floor/ceiling fields
 * @param updates - The updates object to check
 * @returns true if any base room field is being updated
 */
export const shouldUpdateBaseRoom = (updates: Record<string, unknown>): boolean => {
  return (
    updates.weekdayPriceFloor !== undefined ||
    updates.weekendPriceFloor !== undefined ||
    updates.weekdayPriceCeiling !== undefined ||
    updates.weekendPriceCeiling !== undefined
  );
};

/**
 * Creates a base room entry object for roomTypeFloorsCeilings
 * Handles string-to-number conversion and null handling for empty strings
 * @param season - The season object containing price floor/ceiling values
 * @param baseRoomTypeId - The ID of the base room type
 * @returns An object with the base room type ID as key and floor/ceiling values
 */
interface SeasonPriceFields {
  weekdayPriceFloor: number | string;
  weekendPriceFloor: number | string;
  weekdayPriceCeiling: number | string;
  weekendPriceCeiling: number | string;
}

export const createBaseRoomEntry = (season: SeasonPriceFields, baseRoomTypeId: string) => {
  return {
    [baseRoomTypeId]: {
      weekdayFloor:
        season.weekdayPriceFloor === ""
          ? null
          : Number(season.weekdayPriceFloor),
      weekendFloor:
        season.weekendPriceFloor === ""
          ? null
          : Number(season.weekendPriceFloor),
      weekdayCeiling:
        season.weekdayPriceCeiling === ""
          ? null
          : Number(season.weekdayPriceCeiling),
      weekendCeiling:
        season.weekendPriceCeiling === ""
          ? null
          : Number(season.weekendPriceCeiling),
    },
  };
};

/**
 * Transforms an array of room types to the backend roomTypeFloorsCeilings object format
 * @param roomTypes - Array of room type objects with rate floor/ceiling properties
 * @returns Object keyed by room type ID with floor/ceiling values
 */
interface RoomTypeWithRates {
  id: string;
  weekdayRateFloor: number | null;
  weekdayRateCeiling: number | null;
  weekendRateFloor: number | null;
  weekendRateCeiling: number | null;
}

interface FloorCeilingEntry {
  weekdayFloor: number | null;
  weekdayCeiling: number | null;
  weekendFloor: number | null;
  weekendCeiling: number | null;
}

export const transformRoomTypesToFloorsCeilings = (roomTypes: RoomTypeWithRates[]) => {
  return roomTypes.reduce((acc: Record<string, FloorCeilingEntry>, roomType: RoomTypeWithRates) => {
    acc[roomType.id] = {
      weekdayFloor: roomType.weekdayRateFloor,
      weekdayCeiling: roomType.weekdayRateCeiling,
      weekendFloor: roomType.weekendRateFloor,
      weekendCeiling: roomType.weekendRateCeiling,
    };
    return acc;
  }, {});
};

/**
 * Gets the original room type value for comparison with current values
 * @param originalSeasons - Array of original season data
 * @param seasonId - The ID of the season to look up
 * @param roomTypeId - The ID of the room type to look up
 * @param field - The field name to retrieve
 * @returns The original value or null if not found
 */
interface SeasonWithRoomTypes {
  id: string;
  roomTypes?: RoomTypeWithRates[];
}

export const getOriginalRoomTypeValue = (
  originalSeasons: SeasonWithRoomTypes[],
  seasonId: string,
  roomTypeId: string,
  field:
    | "weekdayRateFloor"
    | "weekendRateFloor"
    | "weekdayRateCeiling"
    | "weekendRateCeiling"
) => {
  const originalSeason = originalSeasons.find((s: SeasonWithRoomTypes) => s.id === seasonId);
  if (!originalSeason) return null;

  const originalRoomType = originalSeason.roomTypes?.find(
    (rt: RoomTypeWithRates) => rt.id === roomTypeId
  );
  return originalRoomType?.[field] ?? null;
};

/**
 * Helper function to normalize values for comparison
 * Converts null/undefined to 0, handles string/number conversion
 */
const normalizeValue = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numValue = Number(value);
  return isNaN(numValue) ? null : numValue;
};

/**
 * Calculates the changed fields for all seasons and room types
 * Used for performance optimization and change tracking
 * @param seasons - Current seasons data
 * @param originalSeasons - Original seasons data for comparison
 * @param newSeasonIds - Set of newly created season IDs that shouldn't show changes initially
 * @returns Object mapping season-roomType keys to their changed field states
 */
interface RoomTypeBaseline {
  weekdayRateFloor: number | null;
  weekendRateFloor: number | null;
  weekdayRateCeiling: number | null;
  weekendRateCeiling: number | null;
}

export const calculateChangedFields = (
  seasons: SeasonWithRoomTypes[],
  originalSeasons: SeasonWithRoomTypes[],
  editedRoomTypeBaselines: Record<string, RoomTypeBaseline> = {}
) => {
  const changes: Record<string, Record<string, boolean>> = {};

  seasons.forEach((season: SeasonWithRoomTypes) => {
    const originalSeason = originalSeasons.find((s: SeasonWithRoomTypes) => s.id === season.id);

    season.roomTypes?.forEach((roomType: RoomTypeWithRates) => {
      const key = `${season.id}-${roomType.id}`;
      const baseline = editedRoomTypeBaselines[key];

      // Skip tracking for seasons not in originalSeasons UNLESS they have a baseline
      // (meaning they've been edited and we need to track changes from the baseline)
      if (!originalSeason && !baseline) {
        changes[key] = {
          weekdayFloor: false,
          weekendFloor: false,
          weekdayCeiling: false,
          weekendCeiling: false,
        };
        return;
      }

      // Get current values
      const currentWeekdayFloor = normalizeValue(roomType.weekdayRateFloor);
      const currentWeekendFloor = normalizeValue(roomType.weekendRateFloor);
      const currentWeekdayCeiling = normalizeValue(roomType.weekdayRateCeiling);
      const currentWeekendCeiling = normalizeValue(roomType.weekendRateCeiling);

      // Use baseline if available (for newly added seasons), otherwise use original
      const originalWeekdayFloor = baseline
        ? normalizeValue(baseline.weekdayRateFloor)
        : normalizeValue(
            getOriginalRoomTypeValue(
              originalSeasons,
              season.id,
              roomType.id,
              "weekdayRateFloor"
            )
          );
      const originalWeekendFloor = baseline
        ? normalizeValue(baseline.weekendRateFloor)
        : normalizeValue(
            getOriginalRoomTypeValue(
              originalSeasons,
              season.id,
              roomType.id,
              "weekendRateFloor"
            )
          );
      const originalWeekdayCeiling = baseline
        ? normalizeValue(baseline.weekdayRateCeiling)
        : normalizeValue(
            getOriginalRoomTypeValue(
              originalSeasons,
              season.id,
              roomType.id,
              "weekdayRateCeiling"
            )
          );
      const originalWeekendCeiling = baseline
        ? normalizeValue(baseline.weekendRateCeiling)
        : normalizeValue(
            getOriginalRoomTypeValue(
              originalSeasons,
              season.id,
              roomType.id,
              "weekendRateCeiling"
            )
          );

      // Compare normalized values
      changes[key] = {
        weekdayFloor: currentWeekdayFloor !== originalWeekdayFloor,
        weekendFloor: currentWeekendFloor !== originalWeekendFloor,
        weekdayCeiling: currentWeekdayCeiling !== originalWeekdayCeiling,
        weekendCeiling: currentWeekendCeiling !== originalWeekendCeiling,
      };
    });
  });

  return changes;
};

/**
 * Gets the differential value for a specific day type (weekday or weekend)
 * @param differential - The differential object or number
 * @param dayType - Either "weekday" or "weekend"
 * @returns The differential percentage as a number
 */
export const getDifferentialForDayType = (
  differential: { weekday?: number | string; weekend?: number | string } | number | null | undefined,
  dayType: "weekday" | "weekend"
): number => {
  if (typeof differential === "object" && differential !== null) {
    return Number(differential[dayType]) || 0;
  }
  // Legacy format: single number applies to both
  return Number(differential) || 0;
};

/**
 * Calculates auto value for a room type based on base value + percentage differential
 * @param baseValue - The base value to calculate from
 * @param roomTypeId - The ID of the room type
 * @param season - The season object containing room types
 * @param dayType - Optional: "weekday" or "weekend" to use day-specific differential
 * @returns The calculated auto value
 */
interface SeasonWithDifferentials {
  roomTypes?: Array<{
    id: string;
    differential?: { weekday?: number | string; weekend?: number | string } | number | null;
  }>;
}

export const calculateAutoValue = (
  baseValue: number,
  roomTypeId: string,
  season: SeasonWithDifferentials | null | undefined,
  dayType?: "weekday" | "weekend"
): number => {
  const roomType = season?.roomTypes?.find((rt) => rt.id === roomTypeId);
  const differential = roomType?.differential;

  // Get the appropriate differential based on day type
  let percentageDiff: number;
  if (dayType && typeof differential === "object" && differential !== null) {
    percentageDiff = Number(differential[dayType]) || 0;
  } else if (typeof differential === "object" && differential !== null) {
    // Default to weekday if no day type specified
    percentageDiff = Number(differential.weekday) || 0;
  } else {
    // Legacy format: single number
    percentageDiff = Number(differential) || 0;
  }

  return Math.round(baseValue * (1 + percentageDiff / 100));
};

/**
 * Maps each base-room price field on a Season to the corresponding rate field on
 * a SeasonRoomType, and the day-type whose differential should drive the rate.
 */
const BASE_TO_RATE_FIELD_MAP = [
  {
    baseField: "weekdayPriceFloor",
    rateField: "weekdayRateFloor",
    dayType: "weekday",
  },
  {
    baseField: "weekendPriceFloor",
    rateField: "weekendRateFloor",
    dayType: "weekend",
  },
  {
    baseField: "weekdayPriceCeiling",
    rateField: "weekdayRateCeiling",
    dayType: "weekday",
  },
  {
    baseField: "weekendPriceCeiling",
    rateField: "weekendRateCeiling",
    dayType: "weekend",
  },
] as const;

interface RoomTypeWithDifferential {
  id: string;
  weekdayRateFloor: number | null;
  weekendRateFloor: number | null;
  weekdayRateCeiling: number | null;
  weekendRateCeiling: number | null;
  differential?:
    | { weekday?: number | string; weekend?: number | string }
    | number
    | null;
}

interface BaseRoomPriceUpdates {
  weekdayPriceFloor?: number | string;
  weekendPriceFloor?: number | string;
  weekdayPriceCeiling?: number | string;
  weekendPriceCeiling?: number | string;
}

/**
 * Re-derives a room type's floor/ceiling rates from updated base room prices.
 * Mirrors the Calendar "Adjust Prices" flow:
 *   - Base room rate equals the new base value.
 *   - Non-base rate = round(baseValue * (1 + differential / 100)).
 *   - If baseValue <= 0, the rate is cleared to null.
 * Only fields included in `baseUpdates` are recalculated; others are preserved.
 */
export const recalculateRoomTypeRatesFromBase = <T extends RoomTypeWithDifferential>(
  roomType: T,
  baseUpdates: BaseRoomPriceUpdates,
  baseRoomTypeId: string
): T => {
  const next: T = { ...roomType };
  for (const { baseField, rateField, dayType } of BASE_TO_RATE_FIELD_MAP) {
    const raw = baseUpdates[baseField];
    if (raw === undefined) continue;

    const baseValue = Number(raw) || 0;

    if (roomType.id === baseRoomTypeId) {
      // Base room rate always mirrors the season's base value, so the expanded
      // base row and the main season row stay in sync.
      next[rateField] = baseValue as T[typeof rateField];
      continue;
    }

    if (baseValue > 0) {
      const diff = getDifferentialForDayType(roomType.differential, dayType);
      next[rateField] = Math.round(baseValue * (1 + diff / 100)) as T[typeof rateField];
    } else {
      // Base value is 0/unset → derived rate is also 0, which PriceInput renders
      // as "-" (matching the base row's display).
      next[rateField] = 0 as T[typeof rateField];
    }
  }
  return next;
};

/**
 * Validates room type pricing against base room pricing and differentials
 * Uses day-appropriate differentials (weekday differential for weekday prices, weekend for weekend)
 * @param roomType - The room type being updated
 * @param baseRoomWeekdayFloor - The base room weekday floor price
 * @param baseRoomWeekdayCeiling - The base room weekday ceiling price
 * @param baseRoomWeekendFloor - The base room weekend floor price
 * @param baseRoomWeekendCeiling - The base room weekend ceiling price
 * @returns Object containing validation results and warnings
 */
interface RoomTypeForValidation {
  weekdayRateFloor: number | null;
  weekdayRateCeiling: number | null;
  weekendRateFloor: number | null;
  weekendRateCeiling: number | null;
  differential?: { weekday?: number | string; weekend?: number | string } | number | null;
}

export const validateRoomTypePricing = (
  roomType: RoomTypeForValidation,
  baseRoomWeekdayFloor: number,
  baseRoomWeekdayCeiling: number,
  baseRoomWeekendFloor: number,
  baseRoomWeekendCeiling: number
) => {
  const warnings: string[] = [];
  const errors: string[] = [];

  // Get day-appropriate differentials
  const weekdayDifferential = getDifferentialForDayType(roomType.differential, "weekday");
  const weekendDifferential = getDifferentialForDayType(roomType.differential, "weekend");

  // Calculate exact values (not rounded) for tolerance checking
  // Use weekday differential for weekday prices, weekend differential for weekend prices
  const exactWeekdayFloor = baseRoomWeekdayFloor * (1 + weekdayDifferential / 100);
  const exactWeekdayCeiling = baseRoomWeekdayCeiling * (1 + weekdayDifferential / 100);
  const exactWeekendFloor = baseRoomWeekendFloor * (1 + weekendDifferential / 100);
  const exactWeekendCeiling = baseRoomWeekendCeiling * (1 + weekendDifferential / 100);

  // Rounded values for display
  const expectedWeekdayFloor = Math.round(exactWeekdayFloor);
  const expectedWeekdayCeiling = Math.round(exactWeekdayCeiling);
  const expectedWeekendFloor = Math.round(exactWeekendFloor);
  const expectedWeekendCeiling = Math.round(exactWeekendCeiling);

  // Rounding tolerance: allow if within $1 of exact value (handles rounding edge cases)
  const ROUNDING_TOLERANCE = 1;

  // Check if weekday room type floor is below expected rate (with tolerance)
  if (
    roomType.weekdayRateFloor &&
    roomType.weekdayRateFloor < exactWeekdayFloor - ROUNDING_TOLERANCE
  ) {
    warnings.push(
      `Weekday floor ($${roomType.weekdayRateFloor}) is below global rate based on ${weekdayDifferential}% differential from base room weekday floor ($${baseRoomWeekdayFloor})`
    );
  }

  // Check if weekend room type floor is below expected rate (with tolerance)
  if (
    roomType.weekendRateFloor &&
    roomType.weekendRateFloor < exactWeekendFloor - ROUNDING_TOLERANCE
  ) {
    warnings.push(
      `Weekend floor ($${roomType.weekendRateFloor}) is below global rate based on ${weekendDifferential}% differential from base room weekend floor ($${baseRoomWeekendFloor})`
    );
  }

  // Check if weekday room type ceiling is below expected rate (with tolerance)
  if (
    roomType.weekdayRateCeiling &&
    roomType.weekdayRateCeiling < exactWeekdayCeiling - ROUNDING_TOLERANCE
  ) {
    warnings.push(
      `Weekday ceiling ($${roomType.weekdayRateCeiling}) is below global rate based on ${weekdayDifferential}% differential from base room weekday ceiling ($${baseRoomWeekdayCeiling})`
    );
  }

  // Check if weekend room type ceiling is below expected rate (with tolerance)
  if (
    roomType.weekendRateCeiling &&
    roomType.weekendRateCeiling < exactWeekendCeiling - ROUNDING_TOLERANCE
  ) {
    warnings.push(
      `Weekend ceiling ($${roomType.weekendRateCeiling}) is below global rate based on ${weekendDifferential}% differential from base room weekend ceiling ($${baseRoomWeekendCeiling})`
    );
  }

  // Check if ceiling is below floor (basic validation)
  if (
    roomType.weekdayRateFloor &&
    roomType.weekdayRateCeiling &&
    roomType.weekdayRateCeiling < roomType.weekdayRateFloor
  ) {
    errors.push(
      `Weekday ceiling ($${roomType.weekdayRateCeiling}) cannot be below global rate floor ($${roomType.weekdayRateFloor})`
    );
  }

  if (
    roomType.weekendRateFloor &&
    roomType.weekendRateCeiling &&
    roomType.weekendRateCeiling < roomType.weekendRateFloor
  ) {
    errors.push(
      `Weekend ceiling ($${roomType.weekendRateCeiling}) cannot be below global rate floor ($${roomType.weekendRateFloor})`
    );
  }

  return {
    isValid: errors.length === 0,
    warnings,
    errors,
    expectedWeekdayFloor,
    expectedWeekdayCeiling,
    expectedWeekendFloor,
    expectedWeekendCeiling,
  };
};
